import { ProductSaving, SavingsFormatEnum } from 'types/types';
import { memo } from 'react';
import { css } from '@emotion/react';
import { Flex, FlexProps } from 'components/box';
import { zIndices } from 'constants/z-indices';

const borderRadiusIdxMap = {
  0: 2,
  1: 2,
  2: 2,
};

const borderRadius = (radius?: boolean[]) =>
  radius
    ? radius.map((r, idx) => (r ? borderRadiusIdxMap[idx] : 0))
    : undefined;

type SavingsBadgeProps = FlexProps & {
  className?: string;
  saving?: ProductSaving;
  isLarge?: boolean[];
  isAccented?: boolean[];
  borderRadiusTopRight?: boolean[];
  borderRadiusTopLeft?: boolean[];
  borderRadiusBottomRight?: boolean[];
  borderRadiusBottomLeft?: boolean[];
  alwaysSmall?: boolean;
  fullSavingsBadge?: boolean;
};

const SavingsBadge = memo(
  ({
    className,
    saving,
    isLarge = [false, false, false],
    isAccented = [],
    borderRadiusTopRight,
    borderRadiusTopLeft,
    borderRadiusBottomRight,
    borderRadiusBottomLeft,
    alwaysSmall,
    fullSavingsBadge,
    ...restProps
  }: SavingsBadgeProps) => {
    if (!saving?.fixed.value) return null;

    const savingsText =
      saving.format === SavingsFormatEnum.Fixed
        ? saving.fixed.formattedValue
        : `${saving.percent}%`;

    const borderRadiusProps = {
      borderTopLeftRadius: borderRadius(borderRadiusTopLeft),
      borderTopRightRadius: borderRadius(borderRadiusTopRight),
      borderBottomRightRadius: borderRadius(borderRadiusBottomRight),
      borderBottomLeftRadius: borderRadius(borderRadiusBottomLeft),
    };

    const badgeProps = alwaysSmall
      ? {
          mt: '6%',
          mr: 0,
          py: '0.5em',
          px: 2,
          borderTopLeftRadius: 1,
          borderBottomLeftRadius: 1,
        }
      : fullSavingsBadge
      ? {
          mt: ['-5%', 0, 0],
          mr: [0, '4%', '4%'],
          py: 0,
          px: 0,
          ...borderRadiusProps,
        }
      : {
          mt: ['6%', null, 0],
          mr: [0, null, '5%'],
          py: ['0.5em', null, 0],
          px: [2, '1em', 0],
          ...borderRadiusProps,
        };

    const accentBackground = isAccented?.map((a, idx) =>
      idx === 0 || a ? 'accent' : 'black'
    );

    return (
      <Flex
        className={className}
        backgroundColor={accentBackground}
        fontFamily="header"
        fontWeight={700}
        fontSize="s"
        color="white"
        textAlign="center"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        {...badgeProps}
        {...restProps}
        css={theme => css`
          position: absolute;
          top: 0;
          right: 0;
          z-index: ${zIndices.SAVINGS_BADGE};
          transition: all 0.3s ease;
          text-transform: uppercase;

          ${!alwaysSmall &&
          css`
            ${fullSavingsBadge &&
            css`
              @media ${theme.mediaQueries.mobileOnly} {
                min-width: 50px;
                height: 50px;
              }

              @media ${theme.mediaQueries.tabletOnly} {
                min-width: 56px;
                height: 56px;
              }
            `}

            @media ${theme.mediaQueries.desktopUp} {
              min-width: ${isLarge[2] ? '56px' : '45px'};
              height: ${isLarge[2] ? '56px' : '45px'};
            }
          `}

          &:hover {
            background-color: ${theme.colors.accent} !important;
          }
        `}
      >
        <div
          css={theme => css`
            display: none;

            ${fullSavingsBadge &&
            css`
              display: block;

              @media ${theme.mediaQueries.tabletOnly} {
                font-size: ${theme.fontSizes.r};
              }

              @media ${theme.mediaQueries.mobileOnly} {
                font-size: ${theme.fontSizes.s};
              }
            `}

            ${!alwaysSmall &&
            css`
              @media ${theme.mediaQueries.desktopUp} {
                display: block;
                font-size: ${isLarge[2]
                  ? theme.fontSizes.r
                  : theme.fontSizes.s};
              }
            `}
          `}
        >
          save
        </div>
        <div>
          <span
            css={theme => css`
              font-size: ${theme.fontSizes.s};
              display: inline-block;
              margin-right: 2px;
              ${isLarge[1] &&
              css`
                padding: ${theme.space[2]}px 0;
              `}

              @media ${theme.mediaQueries.mobileOnly} {
                padding: ${isLarge[0] ? `${theme.space[2]}px 0` : 'unset'};
              }

              ${fullSavingsBadge &&
              css`
                @media ${theme.mediaQueries.tabletDown} {
                  display: none;
                }
              `}

              @media ${theme.mediaQueries.desktopUp} {
                display: none;
              }
            `}
          >
            -
          </span>
          <span
            css={theme => css`
              ${fullSavingsBadge &&
              css`
                @media ${theme.mediaQueries.mobileOnly} {
                  font-size: ${theme.fontSizes.r};
                }

                @media ${theme.mediaQueries.tabletOnly} {
                  font-size: ${theme.fontSizes.m};
                }
              `}
              ${isLarge[0] &&
              css`
                font-size: ${theme.fontSizes.r};
              `}

              ${isLarge[1] &&
              css`
                @media ${theme.mediaQueries.tabletOnly} {
                  font-size: ${theme.fontSizes.r};
                }
              `}

              @media ${theme.mediaQueries.desktopUp} {
                padding: 0 ${theme.space[1]}px;
                font-size: ${isLarge[2]
                  ? theme.fontSizes.m
                  : theme.fontSizes.r};
              }
            `}
          >
            {savingsText}
          </span>
        </div>
      </Flex>
    );
  }
);

export default SavingsBadge;
